import { config } from '@/config';
import { makeFetcher } from '@/utils/fetch';
import {
    ProfessionalInfo,
    ProfessionalInfoSubmit,
    VerificationDocument,
    VerificationInfo,
} from '@/types/types';
import { getJson } from '@/utils/fp';

const makeVerificationService = (apiUrl: string) => {
    const fetcher = makeFetcher(apiUrl);
    const multiPartFetcher = makeFetcher(apiUrl, {});

    return {
        getVerificationInfo: async (requestToken: string): Promise<VerificationInfo> => {
            const verificationInfo = (await fetcher
                .get(`/${requestToken}`, { disableRedirects: true })
                .then(getJson)) as VerificationInfo;
            return adaptVerificationInfo(apiUrl, requestToken, verificationInfo);
        },

        saveProfessionalInfo: async (
            requestToken: string,
            professionalInfo: ProfessionalInfoSubmit[],
        ) =>
            fetcher
                .put(`/${requestToken}/professional`, JSON.stringify({ professionalInfo }))
                .then(getJson) as Promise<ProfessionalInfo[]>,

        deleteDocumentById: async (requestToken: string, documentId: number) =>
            fetcher.delete(`/${requestToken}/documents/${documentId}`),

        uploadDocuments: async (
            requestToken: string,
            files: File[],
        ): Promise<VerificationDocument[]> => {
            return multiPartFetcher
                .post(`/${requestToken}/documents`, createFileFormRequest(files))
                .then(getJson) as Promise<VerificationDocument[]>;
        },
    };
};

function createFileFormRequest(files: File[]) {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    return form;
}

function adaptVerificationInfo(
    apiUrl: string,
    requestToken: string,
    verificationInfo: VerificationInfo,
): VerificationInfo {
    const documents = verificationInfo.documents
        ? verificationInfo.documents.map((document) =>
              setDocumentLink(apiUrl, requestToken, document),
          )
        : [];
    return {
        ...verificationInfo,
        documents,
    };
}

function setDocumentLink(
    apiUrl: string,
    requestToken: string,
    document: VerificationDocument,
): VerificationDocument {
    return {
        ...document,
        link: `${apiUrl}/${requestToken}/documents/${document.id}`,
    };
}

export const verificationService = makeVerificationService(config.api.url.verification);
